// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — "Put this globe in your room": the sheet a phone shows the first
 * time Enter AR is pressed (the AR refresh, 2026-10-04). On a phone the
 * button used to be a bare 🥽, and AR began with no word about what it
 * offers or what it needs. The sheet says both — what comes along (the
 * dataset, Browse, layers, colours, pins), how to turn the globe, and that
 * it needs Chrome on Android with Google Play Services for AR — and its
 * Start AR is the user gesture the session request needs. Shown once per
 * browser; Not now leaves it to be shown next time.
 */

import { t } from '../i18n'
import { escapeHtml } from './domUtils'

const SEEN_KEY = 'ayni-ar-intro-seen'

/** Whether this browser has seen the sheet (and started AR from it). */
export function arIntroSeen(): boolean {
  try {
    return localStorage.getItem(SEEN_KEY) === '1'
  } catch {
    return false
  }
}

function markSeen(): void {
  try {
    localStorage.setItem(SEEN_KEY, '1')
  } catch {
    /* private mode: shown again next time, which is harmless */
  }
}

const FEATURE_ICONS = [
  '<rect x="4" y="4" width="7" height="7" rx="1.5"/><rect x="13" y="4" width="7" height="7" rx="1.5"/><rect x="4" y="13" width="7" height="7" rx="1.5"/><rect x="13" y="13" width="7" height="7" rx="1.5"/>',
  '<path d="M12 21s-6-5.5-6-11a6 6 0 0 1 12 0c0 5.5-6 11-6 11z"/><circle cx="12" cy="10" r="2.2"/>',
  '<path d="M12 3v4M12 17v4M3 12h4M17 12h4"/><circle cx="12" cy="12" r="4"/>',
]

/**
 * Show the sheet. `onStart` runs from Start AR's click, so it may request
 * the session; the sheet is gone by then.
 */
export function openArIntroSheet(opts: { datasetTitle: string | null; onStart: () => void; trigger?: HTMLElement | null }): void {
  document.getElementById('xr-intro')?.remove()
  const wrap = document.createElement('div')
  wrap.id = 'xr-intro'
  wrap.className = 'xr-intro'
  const features = ([
    ['vr.intro.f1.title', 'vr.intro.f1.text'],
    ['vr.intro.f2.title', 'vr.intro.f2.text'],
    ['vr.intro.f3.title', 'vr.intro.f3.text'],
  ] as const).map(([title, text], i) => `
      <li class="xr-intro-feature">
        <span class="xr-intro-icon" aria-hidden="true"><svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">${FEATURE_ICONS[i]}</svg></span>
        <span><strong>${escapeHtml(t(title))}</strong><span>${escapeHtml(t(text))}</span></span>
      </li>`).join('')
  const subtitle = opts.datasetTitle
    ? t('vr.intro.subtitle', { title: opts.datasetTitle })
    : t('vr.intro.subtitleEmpty')
  wrap.innerHTML = `
    <div class="xr-intro-backdrop"></div>
    <section class="xr-intro-sheet" role="dialog" aria-modal="true" aria-labelledby="xr-intro-title">
      <div class="xr-intro-grabber" aria-hidden="true"></div>
      <p class="xr-intro-kicker">${escapeHtml(t('vr.intro.kicker'))}</p>
      <h2 id="xr-intro-title" class="xr-intro-title">${escapeHtml(t('vr.intro.title'))}</h2>
      <p class="xr-intro-subtitle">${escapeHtml(subtitle)}</p>
      <ul class="xr-intro-features">${features}</ul>
      <p class="xr-intro-needs">${escapeHtml(t('vr.intro.needs'))}</p>
      <div class="xr-intro-actions">
        <button type="button" class="xr-intro-start">${escapeHtml(t('vr.intro.start'))}</button>
        <button type="button" class="xr-intro-later">${escapeHtml(t('vr.intro.notNow'))}</button>
      </div>
    </section>`
  document.body.appendChild(wrap)
  const start = wrap.querySelector<HTMLButtonElement>('.xr-intro-start')!
  const later = wrap.querySelector<HTMLButtonElement>('.xr-intro-later')!
  const close = (): void => {
    wrap.remove()
    document.removeEventListener('keydown', onKey)
    opts.trigger?.focus()
  }
  const onKey = (ev: KeyboardEvent): void => {
    if (ev.key === 'Escape') close()
  }
  start.addEventListener('click', () => {
    markSeen()
    close()
    opts.onStart()
  })
  later.addEventListener('click', close)
  wrap.querySelector('.xr-intro-backdrop')?.addEventListener('click', close)
  document.addEventListener('keydown', onKey)
  start.focus()
}
